'use client';

import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import type {
  ExplorerFoundationPage,
  ExplorerFoundationResult,
  ExplorerPageId,
} from '@/lib/explorer-foundation';
import ExplorerNav from './components/ExplorerNav';
import ExplorerPolicyBanner from './components/ExplorerPolicyBanner';
import ExplorerPageContent from './components/ExplorerPageContent';

export default function ExplorerLayout({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<ExplorerFoundationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadFoundation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/runtime/explorer-foundation', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = (await response.json()) as ExplorerFoundationResult;
      setData(payload);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Unavailable');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFoundation();
  }, [loadFoundation]);

  return (
    <AppLayout data-component-id="src/app/explorer/layout.tsx:42:4" data-component-path="src/app/explorer/layout.tsx" data-component-line="42" data-component-end-line="81" data-component-file="layout.tsx" data-component-name="AppLayout" data-component-content="%7B%22elementName%22%3A%22AppLayout%22%7D"
      title="RTWO Explorer"
      subtitle="RTWO-EXPA-001 · Step 21 Explorer & Public API · Read-only · Truthful data only"
    >
      <div data-component-id="src/app/explorer/layout.tsx:46:6" data-component-path="src/app/explorer/layout.tsx" data-component-line="46" data-component-end-line="80" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
        <div data-component-id="src/app/explorer/layout.tsx:47:8" data-component-path="src/app/explorer/layout.tsx" data-component-line="47" data-component-end-line="58" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20gap-3%22%7D" className="flex items-center justify-between gap-3">
          <p data-component-id="src/app/explorer/layout.tsx:48:10" data-component-path="src/app/explorer/layout.tsx" data-component-line="48" data-component-end-line="50" data-component-file="layout.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Architecture%20v1.0%20frozen%20%C2%B7%20No%20public%20networking%20%C2%B7%20No%20blockchain%20write%20operations%22%7D" className="text-sm text-muted-foreground">
            Architecture v1.0 frozen · No public networking · No blockchain write operations
          </p>
          <button data-component-id="src/app/explorer/layout.tsx:51:10" data-component-path="src/app/explorer/layout.tsx" data-component-line="51" data-component-end-line="57" data-component-file="layout.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22rounded-md%20border%20border-border%20px-3%20py-1.5%20text-xs%20font-medium%20hover%3Abg-muted%2F40%22%2C%22textContent%22%3A%22Refresh%22%7D"
            type="button"
            onClick={() => void loadFoundation()}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
          >
            Refresh
          </button>
        </div>

        <ExplorerNav data-component-id="src/app/explorer/layout.tsx:60:8" data-component-path="src/app/explorer/layout.tsx" data-component-line="60" data-component-end-line="60" data-component-file="layout.tsx" data-component-name="ExplorerNav" data-component-content="%7B%22elementName%22%3A%22ExplorerNav%22%7D" />

        {loading ? (
          <div data-component-id="src/app/explorer/layout.tsx:63:10" data-component-path="src/app/explorer/layout.tsx" data-component-line="63" data-component-end-line="65" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-border%20bg-card%20p-6%20text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20explorer%20foundation%E2%80%A6%22%7D" className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading explorer foundation…
          </div>
        ) : null}

        {error ? (
          <div data-component-id="src/app/explorer/layout.tsx:69:10" data-component-path="src/app/explorer/layout.tsx" data-component-line="69" data-component-end-line="71" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-negative%2F30%20bg-negative%2F5%20p-6%20text-sm%20text-negative%22%2C%22textContent%22%3A%22Explorer%20foundation%20unavailable%3A%22%7D" className="rounded-xl border border-negative/30 bg-negative/5 p-6 text-sm text-negative">
            Explorer foundation unavailable: {error}
          </div>
        ) : null}

        {data ? (
          <>
            <ExplorerPolicyBanner data-component-id="src/app/explorer/layout.tsx:76:12" data-component-path="src/app/explorer/layout.tsx" data-component-line="76" data-component-end-line="76" data-component-file="layout.tsx" data-component-name="ExplorerPolicyBanner" data-component-content="%7B%22elementName%22%3A%22ExplorerPolicyBanner%22%7D" source={data.source} collectedAt={data.collectedAt} />
            {children}
          </>
        ) : null}
      </div>
    </AppLayout>
  );
}

export function ExplorerSectionPage({ pageId }: { pageId: ExplorerPageId }) {
  const [page, setPage] = useState<ExplorerFoundationPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPage() {
      try {
        const response = await fetch('/api/runtime/explorer-foundation', { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const payload = (await response.json()) as ExplorerFoundationResult;
        const match = payload.pages.find((entry) => entry.id === pageId) ?? null;
        if (!cancelled) {
          setPage(match);
          setError(match ? null : 'Unavailable');
        }
      } catch (fetchError) {
        if (!cancelled) {
          setPage(null);
          setError(fetchError instanceof Error ? fetchError.message : 'Unavailable');
        }
      }
    }

    void loadPage();

    return () => {
      cancelled = true;
    };
  }, [pageId]);

  if (error) {
    return (
      <div data-component-id="src/app/explorer/layout.tsx:121:6" data-component-path="src/app/explorer/layout.tsx" data-component-line="121" data-component-end-line="123" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-negative%2F30%20bg-negative%2F5%20p-6%20text-sm%20text-negative%22%2C%22textContent%22%3A%22Explorer%20page%20unavailable%3A%22%7D" className="rounded-xl border border-negative/30 bg-negative/5 p-6 text-sm text-negative">
        Explorer page unavailable: {error}
      </div>
    );
  }

  if (!page) {
    return (
      <div data-component-id="src/app/explorer/layout.tsx:129:6" data-component-path="src/app/explorer/layout.tsx" data-component-line="129" data-component-end-line="131" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20border%20border-border%20bg-card%20p-6%20text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20explorer%20page%E2%80%A6%22%7D" className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading explorer page…
      </div>
    );
  }

  return <ExplorerPageContent data-component-id="src/app/explorer/layout.tsx:135:9" data-component-path="src/app/explorer/layout.tsx" data-component-line="135" data-component-end-line="135" data-component-file="layout.tsx" data-component-name="ExplorerPageContent" data-component-content="%7B%22elementName%22%3A%22ExplorerPageContent%22%7D" page={page} />;
}
